/**
 * Confine keyboard focus to a container until released.
 *
 * Modal dialogs need to stop Tab traversal from escaping the dialog and
 * landing on background content (which would also be invisible to AT thanks
 * to `aria-modal="true"`). This helper:
 *
 *  - Captures Tab / Shift-Tab and cycles focus through the container's
 *    focusable descendants.
 *  - Remembers the element that had focus when the trap was installed and
 *    restores it on release, so dismissing the dialog returns focus to the
 *    triggering control — the WAI-ARIA APG dialog pattern.
 *  - Survives DOM mutations inside the container by re-querying on each Tab
 *    rather than caching the list at install time.
 *
 * The trap is intentionally minimal: no shim for legacy IE quirks, no
 * polyfill for `inert`, and no automatic initial-focus assignment (callers
 * focus what they want inside `open`, which is more predictable than "the
 * first focusable element" — see `dialogs.ts` for the input-first vs.
 * confirm-first choices).
 */

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  "iframe",
  "object",
  "embed",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable]:not([contenteditable='false'])",
].join(",");

export interface FocusTrapHandle {
  /** Tear down the trap and restore previous focus. Idempotent. */
  readonly release: () => void;
}

function getFocusable(container: HTMLElement): HTMLElement[] {
  const nodes = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
  // Filter out anything hidden via display/visibility — querySelectorAll won't
  // do this for us. `offsetParent === null` is a cheap-and-cheerful check that
  // covers display:none / detached subtrees; happy-dom approximates this too.
  return nodes.filter((n) => !n.hasAttribute("hidden") && n.tabIndex !== -1);
}

// Stack of installed traps. Only the topmost is active — when a dialog opens
// a sub-dialog (e.g. GameOverlay → confirmDialog), the inner trap is pushed
// on top and the outer one stops intercepting Tab until the inner releases.
// Without this, two capture-phase listeners on document race and each yanks
// focus back into its own container, breaking Tab cycling in the inner modal.
interface TrapEntry {
  readonly container: HTMLElement;
}

const trapStack: TrapEntry[] = [];

export function installFocusTrap(container: HTMLElement): FocusTrapHandle {
  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;
  let released = false;
  const entry: TrapEntry = { container };

  function onKeydown(e: KeyboardEvent): void {
    if (e.key !== "Tab") return;
    // Only the topmost trap acts. The shared keydown listener below dispatches
    // to whichever trap is on top, so this guard is belt-and-braces — once
    // installed, each closure is only invoked via the stack lookup anyway.
    if (trapStack[trapStack.length - 1] !== entry) return;
    const focusables = getFocusable(container);
    if (focusables.length === 0) {
      // No focusable target inside — swallow Tab so focus can't drift out.
      e.preventDefault();
      return;
    }
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    const active = document.activeElement;
    // If focus is outside the container (e.g. it never landed inside), pull
    // it back in on Tab. This recovers from stray focus loss mid-modal.
    if (!(active instanceof HTMLElement) || !container.contains(active)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Bind the listener to *this* trap's closure but route through the stack:
  // only the topmost trap actually responds.
  function dispatch(e: KeyboardEvent): void {
    const top = trapStack[trapStack.length - 1];
    if (top !== entry) return;
    onKeydown(e);
  }

  trapStack.push(entry);
  document.addEventListener("keydown", dispatch, true);

  return {
    release(): void {
      if (released) return;
      released = true;
      document.removeEventListener("keydown", dispatch, true);
      // Remove this entry from anywhere in the stack — releases usually
      // happen top-down, but a defensive splice handles out-of-order release
      // without leaving a phantom blocker.
      const idx = trapStack.lastIndexOf(entry);
      if (idx >= 0) trapStack.splice(idx, 1);
      // Restore prior focus only if nothing else has claimed it since release.
      // (Some flows — e.g. a dialog that opens another dialog — explicitly
      // shift focus before release, and we shouldn't stomp that.)
      if (previouslyFocused !== null && document.body.contains(previouslyFocused)) {
        try {
          previouslyFocused.focus();
        } catch {
          // Ignore — element may be in a state that rejects focus.
        }
      }
    },
  };
}
